import { useCallback, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useCreateChannelContext } from '@sendbird/uikit-react/CreateChannel/context';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslate } from 'ra-core';
import { useCreateChannel } from '../../../api/hooks/sendbird/useCreateChannel';
import { useCurrentUser } from '../../../auth/currentUserContext';
import { Button } from '../../../components/ui/button';
import { useGetUsers, type UserDto } from '../../../hooks/useGetUsers';
import { ChannelUserInviteCard } from '../ChannelUserInviteCard';
import { ChannelSettingsModal } from '../GroupCustomChannelSettings/ChannelSettingsModal';
import { SearchTextInput } from '../SearchTextInput';
import { UserInfiniteList } from '../UserInfiniteList';
import { useGoToChannel } from '../useGoToChannel';
import { useHasPermission } from '../../../auth/useHasPermission';
import { CHANNEL_TYPE } from './types';

/**
 * @see https://sendbird.com/docs/chat/platform-api/v3/channel/creating-a-channel/create-a-group-channel#1-create-a-group-channel
 * Max 100 members in a group channel, 200000 for supergroup
 */
export const GROUP_CHANNEL_MAX_NUMBER_OF_MEMBERS = 100;
export const SUPERGROUP_CHANNEL_MAX_NUMBER_OF_MEMBERS = 20_000;
export const ONE_TO_ONE_MAX_NUMBER_OF_MEMBERS = 2;

type CreateGroupsModalProps = {
  open: boolean;
  onClose: () => void;
};

export const CreateGroupsModal = ({
  open,
  onClose,
}: CreateGroupsModalProps) => {
  const translate = useTranslate();
  const [searchTerm, setSearchTerm] = useState('');
  const {
    data: users,
    isLoading,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
    hasNextPage,
    error,
    isError,
    isFetchNextPageError,
    isRefetching,
  } = useInfiniteQuery(useGetUsers({ query: searchTerm }));
  const [selectedUsers, setSelectedUsers] = useState<UserDto[]>([]);
  const currentUser = useCurrentUser();
  const { type } = useCreateChannelContext();
  const goToChannel = useGoToChannel();
  const canCreateMultiUserChannel = useHasPermission(
    'CHAT_CHANNEL_CREATE_MULTI_USERS'
  );

  const isSuperGroup = type === CHANNEL_TYPE.SUPERGROUP;

  const maxUsers = useMemo(() => {
    const maxMembers = canCreateMultiUserChannel
      ? isSuperGroup
        ? SUPERGROUP_CHANNEL_MAX_NUMBER_OF_MEMBERS
        : GROUP_CHANNEL_MAX_NUMBER_OF_MEMBERS
      : ONE_TO_ONE_MAX_NUMBER_OF_MEMBERS;

    // The current user is a member but is not included in the selection list.
    return maxMembers - 1;
  }, [canCreateMultiUserChannel, isSuperGroup]);

  const flattenedUsers = useMemo(() => {
    if (!users?.pages) return [];

    const flattenedUsers = users.pages.flatMap((page) => page.data);

    // Don't include the current user in the list of users to select from
    return flattenedUsers
      .filter((user) => user.id !== currentUser.id)
      .map((user) => ({ ...user, userId: user.id.toString() }));
  }, [currentUser.id, users]);

  const handleCreateChannelSuccess = useCallback(
    async (newChannelUrl: string) => {
      setSearchTerm('');
      setSelectedUsers([]);
      onClose();
      await goToChannel(newChannelUrl);
    },
    [goToChannel, onClose]
  );

  const { mutate: createChannel, isPending: isCreatingChannel } =
    useCreateChannel({
      onSuccess: handleCreateChannelSuccess,
    });

  const handleCreateGroup = useCallback(() => {
    createChannel({
      selectedUsers,
      type,
      currentUser,
    });
  }, [selectedUsers, currentUser, type, createChannel]);

  const handleCancel = () => {
    setSelectedUsers([]);
    onClose();
  };

  return (
    <ChannelSettingsModal
      open={open}
      onCancel={handleCancel}
      title={translate('chat.createGroupsModalTitle')}
      header={
        <SearchTextInput
          label={
            type.toString() === CHANNEL_TYPE.GROUP.toString()
              ? translate('chat.searchNewGroup')
              : translate('chat.searchNewSupergroup')
          }
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
      }
      footer={
        <>
          <Button type="button" variant="ghost" onClick={handleCancel}>
            {translate('chat.close')}
          </Button>
          <Button
            type="button"
            onClick={handleCreateGroup}
            disabled={isCreatingChannel || selectedUsers.length === 0}
          >
            {isCreatingChannel && <Loader2 className="animate-spin" />}
            {translate('chat.create', {
              selected: selectedUsers.length + 1,
              max: canCreateMultiUserChannel
                ? isSuperGroup
                  ? SUPERGROUP_CHANNEL_MAX_NUMBER_OF_MEMBERS
                  : GROUP_CHANNEL_MAX_NUMBER_OF_MEMBERS
                : ONE_TO_ONE_MAX_NUMBER_OF_MEMBERS,
            })}
          </Button>
        </>
      }
    >
      <UserInfiniteList
        users={flattenedUsers}
        isLoadingUsers={isFetchingNextPage}
        isPendingUsers={isLoading}
        refetchUsers={refetch}
        fetchNextPageUsers={fetchNextPage}
        errorUsers={error}
        isErrorUsers={isError}
        isFetchNextPageError={isFetchNextPageError}
        isRefetching={isRefetching}
        hasNextPageUsers={hasNextPage}
        renderUser={(user) => (
          <ChannelUserInviteCard
            user={user}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            maxUsers={maxUsers}
          />
        )}
      />
    </ChannelSettingsModal>
  );
};
